/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { alpha, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type { SimProgressPhase } from './progress';

export type ProgressTone = 'primary' | 'success' | 'error' | 'neutral';

export const PHASE_TONE: Record<Exclude<SimProgressPhase, 'hidden'>, ProgressTone> = {
  starting: 'primary',
  running: 'primary',
  done: 'success',
  failed: 'error',
  cancelled: 'neutral',
};

export const tabularSx = { fontVariantNumeric: 'tabular-nums' } as const;

/** Thin rounded bar in the tone's colour over a faint track. */
export const getTrackSx = (theme: Theme, tone: ProgressTone, height = 4) => ({
  height,
  borderRadius: height / 2,
  backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.1),
  '& .MuiLinearProgress-bar': {
    borderRadius: height / 2,
    backgroundColor: tone === 'neutral' ? theme.palette.text.disabled : theme.palette[tone].main,
    transitionDuration: '400ms',
  },
});

/** Glass pill matching the top-bar icon buttons (`.iconbtn.glass.soft`). */
export const getPillSx = (theme: Theme, tone: ProgressTone): SystemStyleObject<Theme> => {
  const isDark = theme.palette.mode === 'dark';
  const { white } = theme.palette.common;
  return {
    height: 30,
    gap: '8px',
    pl: '10px',
    pr: '12px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    color: 'text.primary',
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.28)' : alpha(white, 0.72),
    border: `1px solid ${
      tone === 'error'
        ? alpha(theme.palette.error.main, 0.55)
        : alpha(isDark ? white : '#0F172A', isDark ? 0.1 : 0.08)
    }`,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    transition: 'background-color 120ms ease, border-color 200ms ease',
    '&:hover': { backgroundColor: isDark ? alpha(white, 0.08) : alpha(white, 0.9) },
    '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '& .MuiSvgIcon-root': { fontSize: 15 },
  };
};

/** The popover surface: the notification popper's glass recipe (surface 92%, strong border). */
export const getPanelSx = (theme: Theme): SystemStyleObject<Theme> => {
  const isDark = theme.palette.mode === 'dark';
  return {
    width: 384,
    maxHeight: 'min(640px, calc(100vh - 80px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: '16px',
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    border: `1px solid ${isDark ? '#2E3D5E' : '#C7D0E4'}`,
    backdropFilter: 'blur(28px)',
    WebkitBackdropFilter: 'blur(28px)',
    boxShadow: isDark ? '0 24px 60px rgba(0, 0, 0, 0.45)' : '0 24px 60px rgba(30, 41, 110, 0.18)',
  };
};

/** Rounded tile behind a row's status icon. */
export const getGlyphSx = (theme: Theme): SystemStyleObject<Theme> => ({
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: '9px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? '#17223A' : '#F5F7FC',
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiSvgIcon-root': { fontSize: 15 },
});

export const sectionLabelSx = {
  px: 2,
  pt: 1.5,
  pb: 0.5,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.disabled',
} as const;
